import React, {
  createContext,
  useState,
  FC,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { PAYWALL_SELECTOR } from 'src/lib/constants';

// we have a set of hard coded keys due to a mistake made with the SOFI and Public launches
// we need to work around these to avoid a poor experience for returning organic users
// see thread: [redacted]########################################################
// TLDR: two unique keys were used for a subfolder and both need to function the same
// this function will union the keys into one while still maintaining the correct behavior
const isUnionedKey = (key): boolean => {
  const unionedKeys = [
    '19aj2304lr', // Public
    '7564838986', // SOFI
  ];
  return unionedKeys.some((unionedKey) => {
    return unionedKey === key;
  });
};

export const PaywallContext = createContext({
  isModalOpen: false,
  setIsModalOpen: (isOpen: boolean) => {
    return null;
  },
  hasPaywall: false,
  handleUnlockContent: () => {
    return null;
  },
  isContentUnlocked: false,
  renderOption: '',
  resetPaywall: () => {
    return null;
  },
  canResetPaywall: false,
});

interface Props {
  children: React.ReactNode;
  cookieName: string;
  unlockKey: string;
  hasInvertedPaywallLogic?: boolean;
  // Every paywall secret on the page, including ones linked from a video card.
  // Reset has to clear those too; the video gate keeps its own provider.
  resetKeys?: string[];
  // Secrets whose paywall uses invert logic. Reset deletes these instead of
  // writing the "false" lock sentinel.
  invertedResetKeys?: string[];
}

export const PaywallProvider: FC<Props> = ({
  children,
  cookieName,
  unlockKey,
  hasInvertedPaywallLogic = false,
  resetKeys = [],
  invertedResetKeys = [],
}) => {
  const [state, setState] = useState({
    isModalOpen: false,
    isContentUnlocked: false,
    hasPaywall: false,
    renderOption: '',
  });

  const handleUnlockContent = useCallback(() => {
    try {
      localStorage.setItem(cookieName, String(unlockKey));
    } catch (e) {
      console.error('local storage not supported'); //eslint-disable-line
    }
    setState((state) => {
      return { ...state, isContentUnlocked: true };
    });
  }, [cookieName, unlockKey]);

  useEffect(() => {
    // Inverted paywalls start open and store the secret, same as a form
    // unlock. ?pwl= stores a lock instead, and that lock sticks after the
    // parameter is removed until the form stores the secret again.
    const urlParams = new URLSearchParams(window.location.search);
    const hasLock = Boolean(urlParams.get('pwl'));
    if (hasInvertedPaywallLogic && !state.isContentUnlocked) {
      if (hasLock) {
        try {
          // Same sentinel as reset. It must not match the secret, or dropping
          // ?pwl= would treat this visit as an unlock.
          localStorage.setItem(cookieName, 'false');
        } catch (e) {
          console.error('local storage not supported'); //eslint-disable-line
        }
      } else {
        let userKey = null;
        try {
          userKey = localStorage.getItem(cookieName);
        } catch (e) {
          console.error('local storage not supported'); //eslint-disable-line
        }
        const hasMatchingKey = userKey === unlockKey || isUnionedKey(userKey);
        if (!userKey || hasMatchingKey) {
          handleUnlockContent();
          return;
        }
      }
    } else if (!hasInvertedPaywallLogic) {
      const hasUnlock = urlParams.get('unlock');
      const hasMatchingBypassKey =
        hasUnlock === unlockKey || isUnionedKey(hasUnlock || '');
      if (unlockKey && hasMatchingBypassKey && !state.isContentUnlocked) {
        handleUnlockContent();
        return;
      }

      try {
        const userKey = localStorage.getItem(cookieName);
        const hasMatchingKey = userKey === unlockKey || isUnionedKey(userKey);
        if (hasMatchingKey && !state.isContentUnlocked) {
          setState((state) => {
            return { ...state, isContentUnlocked: true };
          });
        }
      } catch (e) {
        console.error('local storage not supported'); //eslint-disable-line
      }
    }

    // to avoid prop drilling we check if the document has a paywall and derive information
    // from the nodes dataset to avoid duplication of work
    const paywalllNode: HTMLElement = document.querySelector(PAYWALL_SELECTOR);
    if (paywalllNode && !state.hasPaywall) {
      const renderOption = paywalllNode.dataset.renderOption;
      setState((state) => {
        return { ...state, hasPaywall: true, renderOption };
      });
    }
  }, [
    cookieName,
    handleUnlockContent,
    hasInvertedPaywallLogic,
    state,
    unlockKey,
  ]);

  const setIsModalOpen = (isModalOpen) => {
    setState((state) => {
      return { ...state, isModalOpen };
    });
  };

  const resetPaywall = () => {
    const keys = new Set(
      [cookieName, ...resetKeys].filter((key) => {
        return Boolean(key);
      }),
    );
    const invertedKeys = new Set(
      hasInvertedPaywallLogic ? keys : invertedResetKeys,
    );
    try {
      keys.forEach((key) => {
        if (invertedKeys.has(key)) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, 'false');
        }
      });
    } catch (e) {
      console.error('local storage not supported'); //eslint-disable-line
    }

    // A reload would read ?unlock= or ?pwl= and apply that again. Drop them first.
    // ?pwl= only matters for inverted gates; leaving it on the URL re-locks them.
    const clearsInvertedKey = [...keys].some((key) => {
      return invertedKeys.has(key);
    });
    const url = new URL(window.location.href);
    const dropParams = clearsInvertedKey ? ['unlock', 'pwl'] : ['unlock'];
    const shouldReplace = dropParams.some((param) => {
      return url.searchParams.has(param);
    });
    if (shouldReplace) {
      dropParams.forEach((param) => {
        url.searchParams.delete(param);
      });
      window.location.replace(url.toString());
      return;
    }

    window.location.reload();
  };

  const value = {
    setIsModalOpen,
    isModalOpen: state.isModalOpen,
    hasPaywall: state.hasPaywall,
    isContentUnlocked: state.isContentUnlocked,
    handleUnlockContent,
    renderOption: state.renderOption,
    resetPaywall,
    canResetPaywall: state.hasPaywall || resetKeys.length > 0,
  };
  return (
    <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>
  );
};

export const usePaywallProvider = () => {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error(
      `usePaywallProvider must be used within a Paywall provider`,
    );
  }
  return context;
};

export default usePaywallProvider;
